// SearchPage — the Search page (pen Q82 "Search and a minimal left menu",
// decided 3 Nov; inspired by GitHub and Slack). Top to bottom: the title,
// one line saying what the open tab holds, the field, the tab row (Sources ·
// Materials with their counts; Sort and Filter at its end, the row sticks
// while the list scrolls), the list (SearchList.tsx).
//
// Tabs say where, the field says what, the list shows what was found. Two
// fixed tabs, exactly one open, no All tab: the counts say whether the
// other tab has matches. The field is one string (searchRows.ts): free
// words, and the chosen filters as pills (Kind on Materials, Type on
// Sources, Archived); × on a pill removes it, × at the field's end empties
// it. Typed words stay when switching tabs; filters belong to their tab.
//
// A projection + intents: the field and the tab are frame state (the URL),
// the sort and the place in the list (pages shown, scroll) the library
// module's, all projected in; every choice reports up. The one local DOM
// effect puts the list back where it was (coming back from an opened item)
// and at the top when the list changes.

export { SearchPage };

import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useLayoutEffect, useRef } from "react";
import type { Library } from "../../app/ports/workspace.js";
import { Button } from "../Button/Button.js";
import { Icon } from "../Icon/Icon.js";
import { IconButton } from "../IconButton/IconButton.js";
import { useFileKindLabel } from "../KnowledgeBank/fileKind.js";
import { Menu, type MenuItem } from "../Menu/Menu.js";
import type { LibraryRowIntent } from "../Search/libraryRowIntent.js";
import type { SearchResult } from "../Search/searchModel.js";
import { ViewColumn, ViewHeader } from "../ViewHeader/ViewHeader.js";
import { type Pill, SearchList } from "./SearchList.js";
import {
    FILE_KINDS,
    type FieldQuery,
    MATERIAL_KINDS,
    type MaterialSortField,
    materialRowsOf,
    printFieldQuery,
    queryWords,
    SEARCH_QUERY_MAX,
    type SearchTab,
    type SortOrder,
    type SourceSortField,
    shownMaterials,
    shownSources,
    sourceRowsOf,
    type TabSort,
} from "./searchRows.js";
import {
    directionOf,
    materialKindIcon,
    tabIcon,
    useSearchWords,
} from "./searchWords.js";
import "./SearchPage.css";

// Where the author is in the list `key` names: how many pages show, and
// the scroll to come back to (0: the top).
type SearchPlace = {
    readonly key: string;
    readonly pages: number;
    readonly scrollTop: number;
};

type SearchPageProps = {
    readonly tab: SearchTab;
    // The field as one string (`?q=`), and the same read.
    readonly query: string;
    readonly field: FieldQuery;
    readonly onQuery: (query: string) => void;
    readonly onTab: (tab: SearchTab) => void;
    // Each tab's sort in use (searchRows' effectiveSort).
    readonly materialSort: TabSort<MaterialSortField>;
    readonly sourceSort: TabSort<SourceSortField>;
    readonly onSortMaterials: (sort: TabSort<MaterialSortField>) => void;
    readonly onSortSources: (sort: TabSort<SourceSortField>) => void;
    readonly place: SearchPlace;
    readonly onShowMore: () => void;
    // How many times Search was summoned; a new one focuses the field.
    readonly summons: number;
    // Everything searchable, projected from the library.
    readonly pool: readonly SearchResult[];
    // The loaded library; null while it is read.
    readonly library: Library | null;
    // Files being uploaded now (a note each until they land).
    readonly uploading: readonly string[];
    // An item opened from the list, scrolled this far.
    readonly onOpen: (result: SearchResult, scrollTop: number) => void;
    readonly onOpenProject: (projectId: string) => void;
    readonly onAddSource: () => void;
    // Set up a project (the empty Materials tab's way forward); null while
    // one is being set up.
    readonly onNewProject: (() => void) | null;
    readonly onRowIntent: (intent: LibraryRowIntent) => void;
};

const SearchPage = ({
    tab,
    query,
    field,
    onQuery,
    onTab,
    materialSort,
    sourceSort,
    onSortMaterials,
    onSortSources,
    place,
    onShowMore,
    summons,
    pool,
    library,
    uploading,
    onOpen,
    onOpenProject,
    onAddSource,
    onNewProject,
    onRowIntent,
}: SearchPageProps): ReactNode => {
    const { t } = useLingui();
    const said = useSearchWords();
    const fileKind = useFileKindLabel();
    const scroller = useRef<HTMLDivElement>(null);
    const typing = queryWords(field.text).length > 0;
    const set = (next: FieldQuery) => onQuery(printFieldQuery(next));

    // Both tabs' rows for the field: the open one lists, both count.
    const materialRows = materialRowsOf(pool, library);
    const sourceRows = sourceRowsOf(pool, library);
    const materials = shownMaterials(materialRows, field, materialSort);
    const sources = shownSources(sourceRows, field, sourceSort);
    const loaded = library !== null;

    // The field has focus when the page opens and each time Search is
    // summoned (⌘K, the menu's Search; owner, 3 Nov), without scrolling it
    // into view.
    const input = useRef<HTMLInputElement>(null);
    // biome-ignore lint/correctness/useExhaustiveDependencies: `summons` is the trigger; each new one focuses.
    useLayoutEffect(() => {
        input.current?.focus({ preventScroll: true });
    }, [summons]);
    // Back where the list was (once it has rows), else the top; and the
    // top again whenever the list changes (field, tab, sort: its key).
    const restored = useRef(false);
    // biome-ignore lint/correctness/useExhaustiveDependencies: runs when the list changes or first has rows; the place is read then.
    useLayoutEffect(() => {
        const el = scroller.current;
        if (el === null || !loaded) return;
        el.scrollTop = restored.current ? 0 : place.scrollTop;
        restored.current = true;
    }, [place.key, loaded]);

    // The pills, in the field's order; the last is the no-match way out.
    const pills: readonly Pill[] = [
        ...(field.kind === undefined
            ? []
            : [
                  {
                      key: "kind",
                      icon: materialKindIcon(field.kind),
                      label: t`Kind`,
                      value: said.kind(field.kind),
                      onRemove: () => set({ ...field, kind: undefined }),
                  },
              ]),
        ...(field.type === undefined
            ? []
            : [
                  {
                      key: "type",
                      icon: "file",
                      label: t`Type`,
                      value: fileKind(field.type),
                      onRemove: () => set({ ...field, type: undefined }),
                  } satisfies Pill,
              ]),
        ...(field.archived
            ? [
                  {
                      key: "archived",
                      icon: "archive",
                      label: t`Archived`,
                      value: "",
                      onRemove: () => set({ ...field, archived: false }),
                  } satisfies Pill,
              ]
            : []),
    ];

    const tabButton = (which: SearchTab, label: string, count: number) => (
        <button
            type="button"
            role="tab"
            className="bilda-search__tab"
            aria-selected={tab === which}
            onClick={() => (tab === which ? undefined : onTab(which))}
        >
            <Icon name={tabIcon(which)} size="sm" />
            <span className="bilda-search__tab-label">{label}</span>
            {loaded ? (
                <span className="bilda-search__tab-count">{count}</span>
            ) : null}
        </button>
    );

    // Sort: the field in use names the button, its arrow the direction.
    // The menu: the fields (Best match first while typing), a separator,
    // then the direction worded for the field (none for Best match).
    const sortMenu = <F extends MaterialSortField | SourceSortField>(
        fields: readonly F[],
        sort: TabSort<F>,
        onSort: (sort: TabSort<F>) => void,
    ): ReactNode => {
        const direction = directionOf(sort.field);
        const name = said.field(sort.field);
        const orders: readonly SortOrder[] =
            direction === "date"
                ? ["descending", "ascending"]
                : ["ascending", "descending"];
        const items: readonly MenuItem[] = [
            ...fields
                .filter((f) => typing || f !== "best")
                .map(
                    (f): MenuItem => ({
                        _tag: "item",
                        label: said.field(f),
                        checked: sort.field === f,
                        onSelect: () =>
                            onSort({
                                field: f,
                                order:
                                    directionOf(f) === "date" || f === "best"
                                        ? "descending"
                                        : "ascending",
                            }),
                    }),
                ),
            ...(direction === "none"
                ? []
                : [
                      { _tag: "separator" } satisfies MenuItem,
                      ...orders.map(
                          (o): MenuItem => ({
                              _tag: "item",
                              label: said.order(direction, o),
                              checked: sort.order === o,
                              onSelect: () => onSort({ ...sort, order: o }),
                          }),
                      ),
                  ]),
        ];
        return (
            <Menu
                align="end"
                contentClassName="bilda-search-menu"
                items={items}
                trigger={
                    <Button
                        variant="ghost"
                        size="md"
                        className="bilda-search__sort"
                        aria-label={t`Sort by ${name}`}
                    >
                        <Icon
                            name={
                                sort.order === "ascending" &&
                                direction !== "none"
                                    ? "sort-ascending"
                                    : "sort"
                            }
                            size="sm"
                        />
                        {name}
                        <Icon name="chevron-down" size="sm" />
                    </Button>
                }
            />
        );
    };

    const filterItems = (): readonly MenuItem[] => {
        switch (tab) {
            case "materials":
                return [
                    { _tag: "label", label: t`Filter by kind` },
                    {
                        _tag: "item",
                        label: t`Any`,
                        checked: field.kind === undefined,
                        onSelect: () => set({ ...field, kind: undefined }),
                    },
                    ...MATERIAL_KINDS.map(
                        (k): MenuItem => ({
                            _tag: "item",
                            label: said.kind(k),
                            checked: field.kind === k,
                            onSelect: () => set({ ...field, kind: k }),
                        }),
                    ),
                ];
            case "sources":
                return [
                    { _tag: "label", label: t`Filter by type` },
                    {
                        _tag: "item",
                        label: t`Any`,
                        checked: field.type === undefined,
                        onSelect: () => set({ ...field, type: undefined }),
                    },
                    ...FILE_KINDS.map(
                        (k): MenuItem => ({
                            _tag: "item",
                            label: fileKind(k),
                            checked: field.type === k,
                            onSelect: () => set({ ...field, type: k }),
                        }),
                    ),
                ];
        }
    };

    const lede =
        tab === "materials"
            ? t`What you've made, from all your projects. Each material lives in one project.`
            : t`What you've brought in. One source can be used in many projects.`;

    return (
        <div className="bilda-search" ref={scroller}>
            <ViewColumn className="bilda-search__column">
                <ViewHeader
                    title={t`Search`}
                    tools={<p className="bilda-search__lede">{lede}</p>}
                />
                <label className="bilda-search__field">
                    <Icon name="search" size="md" />
                    {pills.map((pill) => {
                        const pillName = `${pill.label} ${pill.value}`.trim();
                        return (
                            <span key={pill.key} className="bilda-search__pill">
                                <Icon name={pill.icon} size="xs" />
                                <span className="bilda-search__pill-label">
                                    {pill.label}
                                </span>
                                {pill.value === "" ? null : (
                                    <span className="bilda-search__pill-value">
                                        {pill.value}
                                    </span>
                                )}
                                <IconButton
                                    label={t`Remove ${pillName}`}
                                    size="sm"
                                    variant="ghost"
                                    onClick={pill.onRemove}
                                >
                                    <Icon name="close" size="xs" />
                                </IconButton>
                            </span>
                        );
                    })}
                    <input
                        ref={input}
                        type="search"
                        value={field.text}
                        maxLength={SEARCH_QUERY_MAX}
                        aria-label={t`Search`}
                        onChange={(e) =>
                            set({ ...field, text: e.currentTarget.value })
                        }
                    />
                    {query === "" ? null : (
                        <IconButton
                            label={t`Clear the search`}
                            size="sm"
                            variant="ghost"
                            className="bilda-search__clear"
                            onClick={() => onQuery("")}
                        >
                            <Icon name="clear-field" size="md" />
                        </IconButton>
                    )}
                </label>
                {uploading.map((filename, i) => (
                    <p
                        // biome-ignore lint/suspicious/noArrayIndexKey: two queued files may share a name; the batch order is stable
                        key={i}
                        className="bilda-search__note"
                        role="status"
                    >
                        {t`Uploading ${filename}…`}
                    </p>
                ))}
                <div className="bilda-search__tabrow">
                    <div
                        role="tablist"
                        aria-label={t`Search in`}
                        className="bilda-search__tabs"
                    >
                        {tabButton("sources", t`Sources`, sources.length)}
                        {tabButton("materials", t`Materials`, materials.length)}
                    </div>
                    <div className="bilda-search__tools">
                        {tab === "materials"
                            ? sortMenu<MaterialSortField>(
                                  [
                                      "best",
                                      "changed",
                                      "name",
                                      "kind",
                                      "project",
                                  ],
                                  materialSort,
                                  onSortMaterials,
                              )
                            : sortMenu<SourceSortField>(
                                  ["best", "added", "name", "type", "used-in"],
                                  sourceSort,
                                  onSortSources,
                              )}
                        <Menu
                            align="end"
                            contentClassName="bilda-search-menu"
                            items={filterItems()}
                            trigger={
                                <Button
                                    variant="ghost"
                                    size="md"
                                    className="bilda-search__filters"
                                >
                                    <Icon name="filter" size="sm" />
                                    {t`Filter`}
                                </Button>
                            }
                        />
                    </div>
                </div>
                <SearchList
                    tab={tab}
                    field={field}
                    words={queryWords(field.text)}
                    library={library}
                    materials={materials}
                    sources={sources}
                    materialRows={materialRows}
                    sourceRows={sourceRows}
                    pills={pills}
                    pages={place.pages}
                    onShowMore={onShowMore}
                    onShowArchived={() => set({ ...field, archived: true })}
                    onClear={() => onQuery("")}
                    onOpen={(result) =>
                        onOpen(result, scroller.current?.scrollTop ?? 0)
                    }
                    onOpenProject={onOpenProject}
                    onAddSource={onAddSource}
                    onNewProject={onNewProject}
                    onRowIntent={onRowIntent}
                />
            </ViewColumn>
        </div>
    );
};
